<template>
    <div>
        <info-box v-if="playerHasOpenGameRound" :openRoundGameId="openRoundGameId" @openGame="openGame" />
        <div class="lobby">
            <div class="flex items-center justify-center flex-wrap h-full w-mobile mx-auto mb-3 md:mb-4" v-if="!gamesContainOnlyOneType">
                <label class="inline-flex items-center mx-2" v-if="showBingoCheckbox">
                    <input @change="filterGames" type="checkbox" v-model="showBingo" class="form-checkbox text-indigo-600" checked>
                    <span class="ml-2 text-gray-300 text-sm">{{$t('Bingo')}}</span>
                </label>
                <label class="inline-flex items-center mx-2">
                    <input @change="filterGames" type="checkbox" v-model="showSlot" class="form-checkbox text-indigo-600" checked>
                    <span class="ml-2 text-gray-300 text-sm">{{$t('Slot')}}</span>
                </label>
            </div>
            <div class="carousel" v-if="gamesReady">
                <carousel
                    :per-page="1"
                    :pagination-enabled="false"
                    @pageChange="onPageChange"
                    ref="carousel"
                    :key="resetCarouselId">
                    <slide v-for="(slide, i) in filteredGames" :key="i">
                        <div class="flex items-center justify-center flex-wrap mx-auto">
                            <div class="game" :class="getGameSize()" v-for="game in slide" :key="game.id">
                                <a @click="openGame(game.id)" class="block text-gray-400 hover:text-gray-200 focus:text-gray-200" >
                                    <div class="game-img-wrapper">
                                        <div class="game-img">
                                            <v-lazy-image
                                                :src="game.imageUrl"
                                                :src-placeholder="game.thumbnailImageUrl"
                                                :alt="game.name"
                                                use-picture
                                            >
                                                <source media="(max-width: 767px)" :srcset="game.mobileImageUrl">
                                                <source media="(min-width: 768px)" :srcset="game.desktopImageUrl">
                                            </v-lazy-image>
                                            <div class="game-play">
                                                <div class="game-play-text">
                                                    SELECT<br>GAME
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </slide>
                </carousel>
                <a class='text-white carousel-nav nav-prev' @click="goToPrev" :class="{'opacity-25': isFirstPage}">
                    <font-awesome-icon icon='chevron-circle-left'/>
                </a>
                <a class='text-white carousel-nav nav-next' @click="goToNext" :class="{'opacity-25': isLastPage}">
                    <font-awesome-icon icon='chevron-circle-right'/>
                </a>
            </div>
            <div v-show="!gamesReady" class="text-gray-200 text-xl text-center">
                <font-awesome-icon icon="spinner" pulse class="mr-2"/>
                {{$t('Please wait')}}
            </div>
            <div v-if="!filteredGames.length" class="mx-2 text-3xl text-bold text-gray-200 text-center">
                <div>
                    <font-awesome-icon icon="ban" class="mb-1"/>
                </div>
                {{ $t('No game found') }}
            </div>
        </div>
    </div>
</template>

<script>
import { Carousel, Slide } from 'vue-carousel';
import _ from 'lodash';
import InfoBox from "../Components/InfoBox";
import GameMixin from "../../Shared/mixin/Game";
import VLazyImage from "v-lazy-image/v2";

export default {
    name: 'Games',
    mixins: [
        GameMixin,
    ],
    props: [
        'games',
        'types',
        'selectedType',
        'playerHasOpenGameRound',
        'openRoundGameId',
        'redirectPlayerToGameWithOpenRound'
    ],
    components: {
        InfoBox,
        Carousel,
        Slide,
        VLazyImage
    },
    metaInfo() {
        return {
            title: this.title
        }
    },
    data() {
        return {
            searchQuery: '',
            filteredGames: _.chunk(this.games, this.gamesInView),
            showTypes: false,
            gamesContainOnlyOneType: false,
            showBingoCheckbox: true,
            typeBingo: 'bingo',
            typeSlot: 'slot',
            showBingo: this.selectedType === this.typeBingo || this.selectedType === 'all',
            showSlot: this.selectedType === this.typeSlot || this.selectedType === 'all',
            gamesInView: 1,
            gamesReady: true,
            currentPage: 0,
            slideCount: 1,
            resetCarouselId: 0,
            fullScreen: false,
            isGameOpening: false,
        }
    },
    computed: {
        title() {
            return this.$t('Games');
        },
        isLastPage() {
            return this.currentPage === this.slideCount - 1;
        },
        isFirstPage() {
            return this.currentPage === 0;
        },
    },
    methods: {
        openGame(gameId) {
            this.openCasinoGame(gameId);
        },
        searchGames() {
            this.filteredGames = this.games.filter(game => {
                return game.name.toLowerCase().includes(this.searchQuery.toLowerCase())
            })
        },
        goToNext() {
            this.$refs.carousel.goToPage(this.$refs.carousel.currentPage + 1);
        },
        goToPrev() {
            this.$refs.carousel.goToPage(this.$refs.carousel.currentPage - 1);
        },
        onPageChange(value) {
            this.slideCount = this.$refs.carousel.slideCount;
            this.currentPage = value;
        },
        resizeGames() {
            let gameWidth, gameHeight;
            let widthPaddings = 32;
            let heightPaddings = 117;
            switch (this.getGameSize()) {
                case 'game-xs':
                    gameWidth = 196;
                    gameHeight = 117.5;
                    widthPaddings = 24;
                    heightPaddings = 93;
                    break;
                case 'game-sm':
                    gameWidth = 286;
                    gameHeight = 162.5;
                    widthPaddings = 24;
                    heightPaddings = 93;
                    break;
                default:
                    gameWidth = 320;
                    gameHeight = 189;
            }
            let gamesInRow = Math.floor((window.innerWidth - widthPaddings) / gameWidth);
            gamesInRow = gamesInRow > 0 ? gamesInRow: 1;
            let gamesInColumn = Math.floor((window.innerHeight - heightPaddings) / gameHeight);
            gamesInColumn = gamesInColumn > 0 ? gamesInColumn: 1;
            this.gamesInView = gamesInRow * gamesInColumn;
        },
        filterGames() {
            this.resizeGames();

            this.showBingoCheckbox = this.games.some(game => game.type === this.typeBingo);
            let firstGameType = this.games[0].type;
            this.gamesContainOnlyOneType = this.games.every(game => game.type === firstGameType);

            this.gamesReady = false;
            this.filteredGames = this.games.filter(game => {
                if (this.showBingo && this.showSlot) {
                    return true;
                }

                if (this.showBingo) {
                    return game.type === this.typeBingo;
                }

                if (this.showSlot) {
                    return game.type === this.typeSlot;
                }

                return false;
            });

            this.slideCount = Math.ceil(this.filteredGames.length / this.gamesInView);
            this.currentPage = 0;
            this.filteredGames = _.chunk(this.filteredGames, this.gamesInView);
            this.$nextTick(() => {
                this.resetCarouselId ++;
                this.gamesReady = true;
            })
        },
        getGameSize() {
            if(window.innerWidth <= 900) {
                if(window.matchMedia("(orientation: landscape)").matches) {
                    return 'game-xs'
                }

                return 'game-sm'
            }
            return '';
        },
        openGameWithUnfinishedRound() {
            if (!this.playerHasOpenGameRound || !this.redirectPlayerToGameWithOpenRound) {
                return;
            }

            this.openGame(this.openRoundGameId);
        },
    },
    mounted() {
        this.filterGames();
    },
    created() {
        this.openGameWithUnfinishedRound();
        this.filterGames();
        window.addEventListener("resize", this.filterGames);
    },
    destroyed() {
        window.removeEventListener("resize", this.filterGames);
    },
}
</script>

<style lang="scss">
.carousel {
    position: relative;

    .carousel-nav {
        position: absolute;
        font-size: 1.5rem;
        top: 50%;
        transform: translateY(-50%);
        cursor: pointer;

        &.nav-prev {
            left: -5px;
        }

        &.nav-next {
            right: -5px;
        }
    }
}

.game {
    width: 300px;
    margin-left: 10px;
    margin-right: 10px;
    margin-bottom: 20px;

    &.game-xs {
        width: 180px;
        margin-left: 8px;
        margin-right: 8px;
        margin-bottom: 16px;

        .game-img {
            width: 180px;
            height: 101.5px;
        }
    }

    &.game-sm {
        width: 260px;
        margin-left: 8px;
        margin-right: 8px;
        margin-bottom: 16px;

        .game-img {
            width: 260px;
            height: 146.5px;
        }
    }

    .game-img {
        display: block;
        height: 169px;
        width: 300px;
        position: relative;
        overflow: hidden;
        border-radius: 10px;
        transition: box-shadow .55s ease-in-out;

        img {
            border-radius: 10px;
            transition: transform .55s ease-in-out;
        }

        .game-play {
            font-size: 4rem;
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            text-align: center;
            background-color: rgba(25, 25, 25, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 10px;
            transition: opacity .55s ease-in-out;
            opacity: 0;

            .game-play-text {
                font-size: 32px;
                font-weight: 600;
                line-height: 1;
                padding: 10px 25px;
                border-radius: 10px;
                box-shadow: 0px 0px 10px 5px #bd10e0;
                background: #ffffff;
                color: #bd10e0;
            }
        }

        &:focus,
        &:hover {
            .game-play {
                opacity: 1;
            }

            img {
                transform: scale(1.1);
                -webkit-transform: scale(1.1);
            }
        }
    }
    .game-img-wrapper {
        border-radius: 10px;
        box-shadow: 0 0 5px 3px rgba(0, 0, 0, 0.15);
        transition: box-shadow .55s ease-in-out;

        &:focus,
        &:hover {
            box-shadow: 0 0 10px 4px rgba(0, 0, 0, 0.15);
        }
    }
}

#search {
    width: 60%;

    input {
        width: 100%;
    }
}

#types {
    width: 40%;
}

</style>
